import React, { useState } from "react";
import { Form, Table, Icon, Dropdown, Popup, Accordion } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { getDuplicateDoorParameterBindings } from "../../utils/pdApiUtils";

type DoorParameterBindingsProps = {
  code: number;
  domainId: number;
  bindings: Api.DoorParameterBinding[];
  handleBindingsChanged: (bindings: Api.DoorParameterBinding[]) => void;
  permissions: boolean;
};

const DoorParameterBindings: React.FC<DoorParameterBindingsProps> = (
  props: DoorParameterBindingsProps
) => {
  const { code, domainId, bindings, handleBindingsChanged, permissions } = props;
  const doorVersions = useSelector((state: State.Root) => state.pdApi.DoorVersions);
  const domains = useSelector((state: State.Root) => state.pdApi.domains);

  const [isOpen, setIsOpen] = useState(false);

  // Filter versions to only those belonging to the parameter's domain
  const versionOptions = (doorVersions ?? [])
    .filter(dv => domainId > 0 ? dv.domain.id === domainId : true)
    .map(dv => ({
      key: dv.id,
      text: `${dv.number}.${domains[dv.domain.id]?.enumName ?? ""}`,
      value: dv.id
    }));

  const duplicateBindings = getDuplicateDoorParameterBindings(bindings);

  const extendedBindings = bindings.map((binding, index) => ({
    ...binding,
    isDuplicate: duplicateBindings.find(i => i === index) !== undefined
  }));

  const addBindings = () => {
    const firstVersion = (doorVersions ?? []).find(dv => domainId > 0 ? dv.domain.id === domainId : true);
    const newBinding = {
      doorParameterCode: code,
      doorVersion: {
        id: firstVersion?.id ?? -1,
        url: firstVersion?.url ?? ""
      },
      isDirty: true
    };
    handleBindingsChanged([newBinding, ...bindings]);
  };

  const handleRemoveBinding = (removedIndex: number): void => {
    const modifiedBindings = bindings.filter((_, index) => index !== removedIndex);
    handleBindingsChanged(modifiedBindings);
  };

  const handleDoorVersionChange = (versionId: number, changedIndex: number): void => {
    const doorVersion = doorVersions?.find(dv => dv.id === versionId);
    if (doorVersion) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            doorVersion: { id: doorVersion.id ?? -1, url: doorVersion.url },
            isDirty: true
          };
        }
        return binding;
      });
      handleBindingsChanged(editedBindings);
    }
  };

  return (
    <>
      <Form.Group id="doorparameterbindings" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title active={isOpen} onClick={() => setIsOpen(!isOpen)}>
              <Icon name="dropdown" />
              <label>
                <b>Parameter bindings</b>
              </label>
              {duplicateBindings.length > 0 && (
                <p className="errorMessagePadding">Bindings should not be empty or duplicated.</p>
              )}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Name</Table.HeaderCell>
                    {!permissions && (
                      <Table.HeaderCell textAlign="center">
                        <Icon
                          id="addDoorBinding"
                          className="color-green"
                          link={true}
                          name="add"
                          onClick={() => addBindings()}
                        />
                      </Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {extendedBindings.map(
                    (binding, index): JSX.Element => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <div className="flex-row">
                            {binding.isDirty ? "*" : ""}
                            <Dropdown
                              id={`doorBinding-${index}`}
                              compact={true}
                              fluid={true}
                              selection={true}
                              options={versionOptions}
                              value={binding.doorVersion.id}
                              error={binding.isDuplicate}
                              onChange={(e, data) =>
                                handleDoorVersionChange(data.value as number, index)
                              }
                              disabled={permissions}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {doorVersions?.find(dv => dv.id === binding.doorVersion.id) && (
                            <Popup
                              trigger={
                                <Icon className="color-blue" link={false} name="info circle" />
                              }
                              content={
                                doorVersions.find(dv => dv.id === binding.doorVersion.id)
                                  ?.versionName
                              }
                            />
                          )}
                        </Table.Cell>
                        {!permissions && (
                          <Table.Cell textAlign="center">
                            <Popup
                              trigger={
                                <Icon
                                  id="removeDoorBinding"
                                  className="color-red"
                                  link={true}
                                  name="remove"
                                  onClick={() => handleRemoveBinding(index)}
                                />
                              }
                              content={"Remove binding"}
                            />
                          </Table.Cell>
                        )}
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    </>
  );
};

export default DoorParameterBindings;
